import type { StateCreator } from "zustand";

import { getEffectiveUserId } from "@/features/auth/current-user-identity";
import type { AppState } from "@/store/app-state";
import type {
  ActionItemsManagementStatus,
  PublicActionItem,
  PublicActionItemEvent,
} from "@/backend/contracts/action-items-client";

import {
  createActionItemWriteOperation,
  createManualActionItem,
  dismissActionItemDraft as enqueueDismissActionItemDraft,
  patchActionItem,
  promoteActionItemDraft,
} from "../sync/action-items-api";
import {
  clearScheduledActionItemPatches,
  getScheduledActionItemPatchOperation,
  scheduleActionItemPatch,
} from "../sync/action-item-patch-scheduler";
import { rollbackPendingActionItemMutation } from "../sync/action-item-pending-writes";
import type { ProjectScope } from "../sync/project-scope";
import {
  canonicalizeActionItemPatch,
  closeActionItem,
  createActionItem,
  newActionItemId,
  reopenActionItem,
} from "./action-item-model.ts";
import {
  createActionItemEvent,
  projectPublicActionItemEvent,
} from "./assignment-model";
import { projectActionItemDraftReview } from "./draft-review-adapters/registry.ts";
import type {
  ActionItemCloseReason,
  ActionItemDraft,
  ActionItemPriority,
  ActionItemReasonLabel,
} from "./types";

export type ActionItemUpdateInput = Partial<
  Pick<
    PublicActionItem,
    | "description"
    | "dueDate"
    | "priority"
    | "department"
    | "reasonLabels"
    | "title"
    | "mergedIntoId"
  >
>;

export interface AddActionItemInput {
  title: string;
  description?: string;
  reasonLabels: readonly ActionItemReasonLabel[];
  priority?: ActionItemPriority;
  dueDate?: string;
  department?: string;
}

interface PromoteActionItemDraftOptions {
  dueDate?: string;
  priority?: ActionItemPriority;
}

export interface ActionItemsSlice {
  actionItems: PublicActionItem[];
  actionItemEvents: PublicActionItemEvent[];
  actionItemDrafts: ActionItemDraft[];
  actionItemsManagementStatus: ActionItemsManagementStatus;
  reviewedActionItemEvidenceKeys: string[];
  actionItemActions: {
    addActionItem: (input: AddActionItemInput) => string;
    updateActionItem: (id: string, updates: ActionItemUpdateInput) => void;
    approveActionItemDraft: (
      draft: ActionItemDraft,
      input?: PromoteActionItemDraftOptions,
    ) => string;
    dismissActionItemDraft: (draft: ActionItemDraft) => void;
    closeActionItem: (id: string, closeReason: ActionItemCloseReason) => void;
    reopenActionItem: (id: string) => void;
    setActionItemsFromRemote: (items: PublicActionItem[]) => void;
    setActionItemEventsFromRemote: (events: PublicActionItemEvent[]) => void;
    setActionItemDrafts: (drafts: ActionItemDraft[]) => void;
    setActionItemsManagementResource: (input: {
      status: ActionItemsManagementStatus;
      reviewedEvidenceKeys: string[];
    }) => void;
    clearActionItems: () => void;
  };
}

function scopeOf(state: AppState & ActionItemsSlice): ProjectScope | null {
  return state.cloud.enabled && state.cloud.orgId && state.cloud.projectId
    ? { orgId: state.cloud.orgId, projectId: state.cloud.projectId }
    : null;
}

function actionItemPatchFromUpdates(updates: ActionItemUpdateInput): {
  patch: ActionItemUpdateInput;
  clearFields: Array<"description" | "dueDate" | "priority" | "department" | "mergedIntoId">;
} {
  return canonicalizeActionItemPatch(updates);
}

function queue(command: () => Promise<unknown>): void {
  void command().catch(() => undefined);
}

function replaceRows<T extends { id: string }>(rows: T[], replacement: T): T[] {
  return rows.some((row) => row.id === replacement.id)
    ? rows.map((row) => (row.id === replacement.id ? replacement : row))
    : [...rows, replacement];
}

function restoreAfterActionItemStorageFailure(
  set: Parameters<
    StateCreator<
      AppState & ActionItemsSlice,
      [["zustand/immer", never]],
      [],
      ActionItemsSlice
    >
  >[0],
  scope: ProjectScope,
): (operationId: string) => void {
  return (operationId) =>
    set((state) => {
      const rollback = rollbackPendingActionItemMutation(scope, operationId, {
        items: state.actionItems,
        events: state.actionItemEvents,
        reviewedEvidenceKeys: state.reviewedActionItemEvidenceKeys,
      });
      if (!rollback) return;
      state.actionItems = rollback.items;
      state.actionItemEvents = rollback.events;
      state.reviewedActionItemEvidenceKeys = rollback.reviewedEvidenceKeys;
    });
}

function optimisticEvent(
  operationId: string,
  actionItemId: string,
  kind: PublicActionItemEvent["kind"],
  summary: string,
  actorId: string,
  occurredAt = new Date().toISOString(),
): PublicActionItemEvent {
  return projectPublicActionItemEvent(createActionItemEvent({
    operationId,
    actionItemId,
    kind,
    summary,
    actorId,
    occurredAt,
  }));
}

export const createActionItemsSlice: StateCreator<
  AppState & ActionItemsSlice,
  [["zustand/immer", never]],
  [],
  ActionItemsSlice
> = (set, get) => ({
  actionItems: [],
  actionItemEvents: [],
  actionItemDrafts: [],
  actionItemsManagementStatus: "loading",
  reviewedActionItemEvidenceKeys: [],
  actionItemActions: {
    addActionItem: (input) => {
      const actorId = getEffectiveUserId() ?? "unknown";
      const item = createActionItem({ ...input, actorId });
      const scope = scopeOf(get());
      const operation =
        scope &&
        createActionItemWriteOperation(
          scope,
          actorId,
          `actionItems/${item.id}`,
        );
      const event =
        operation &&
        optimisticEvent(
          operation.id,
          item.id,
          "item_created",
          "Action item created.",
          actorId,
        );
      set((state) => {
        state.actionItems.push(item);
        if (event)
          state.actionItemEvents = replaceRows(state.actionItemEvents, event);
      });
      if (scope && operation && event)
        queue(() =>
          createManualActionItem({
            scope,
            actorId,
            operation,
            actionItemId: item.id,
            ...input,
            optimistic: { items: [item], events: [event], reviewedEvidenceKeys: [] },
            prior: { items: [], events: [], reviewedEvidenceKeys: [] },
            onStorageFailure: restoreAfterActionItemStorageFailure(set, scope),
          }),
        );
      return item.id;
    },
    approveActionItemDraft: (draft, input = {}) => {
      const projection = projectActionItemDraftReview(draft);
      if (!projection) return "";
      const actorId = getEffectiveUserId() ?? "unknown";
      const reviewId = projection.evidenceKey;
      if (get().reviewedActionItemEvidenceKeys.includes(reviewId))
        return "";
      const itemId = newActionItemId();
      const now = new Date().toISOString();
      const item: PublicActionItem = {
        ...createActionItem({
          id: itemId,
          title: draft.title,
          reasonLabels: ["explicit_action"],
          actorId,
          nowIso: now,
          ...input,
        }),
        provenance: {
          kind: "draftReview",
        },
      };
      const scope = scopeOf(get());
      const operation =
        scope &&
        createActionItemWriteOperation(
          scope,
          actorId,
          `actionItemDraftReviews/${reviewId}`,
        );
      const event =
        operation &&
        optimisticEvent(
          operation.id,
          itemId,
          "draft_promoted",
          "Draft promoted to action item.",
          actorId,
          now,
        );
      set((state) => {
        state.actionItems.push(item);
        state.reviewedActionItemEvidenceKeys.push(reviewId);
        if (event)
          state.actionItemEvents = replaceRows(state.actionItemEvents, event);
      });
      if (scope && operation && event)
        queue(() =>
          promoteActionItemDraft({
            scope,
            actorId,
            operation,
            draftLocator: projection.locator,
            evidenceKey: projection.evidenceKey,
            actionItemId: itemId,
            title: item.title,
            reasonLabels: item.reasonLabels,
            ...input,
            optimistic: {
              items: [item],
              events: [event],
              reviewedEvidenceKeys: [reviewId],
            },
            prior: { items: [], events: [], reviewedEvidenceKeys: [] },
            onStorageFailure: restoreAfterActionItemStorageFailure(set, scope),
          }),
        );
      return itemId;
    },
    dismissActionItemDraft: (draft) => {
      const projection = projectActionItemDraftReview(draft);
      if (!projection) return;
      const actorId = getEffectiveUserId() ?? "unknown";
      const reviewId = projection.evidenceKey;
      const scope = scopeOf(get());
      const operation =
        scope &&
        createActionItemWriteOperation(
          scope,
          actorId,
          `actionItemDraftReviews/${reviewId}`,
        );
      set((state) => {
        state.reviewedActionItemEvidenceKeys = [
          ...state.reviewedActionItemEvidenceKeys.filter((key) => key !== reviewId),
          reviewId,
        ];
      });
      // The resolver deliberately records no event for a dismissal.
      if (scope && operation)
        queue(() =>
          enqueueDismissActionItemDraft({
            scope,
            actorId,
            operation,
            draftLocator: projection.locator,
            evidenceKey: projection.evidenceKey,
            optimistic: { items: [], events: [], reviewedEvidenceKeys: [reviewId] },
            prior: { items: [], events: [], reviewedEvidenceKeys: [] },
            onStorageFailure: restoreAfterActionItemStorageFailure(set, scope),
          }),
        );
    },
    updateActionItem: (id, updates) => {
      const actorId = getEffectiveUserId() ?? "unknown";
      const previous = get().actionItems.find((item) => item.id === id);
      if (!previous) return;
      const { patch, clearFields } = actionItemPatchFromUpdates(updates);
      const next: PublicActionItem = {
        ...previous,
        ...patch,
        updatedAt: new Date().toISOString(),
        updatedBy: actorId,
      };
      for (const field of clearFields) delete next[field];
      const scope = scopeOf(get());
      const operation =
        scope &&
        (getScheduledActionItemPatchOperation(scope, id) ??
          createActionItemWriteOperation(scope, actorId, `actionItems/${id}`));
      const event =
        operation &&
        optimisticEvent(
          operation.id,
          id,
          "item_updated",
          "Action item updated.",
          actorId,
        );
      set((state) => {
        state.actionItems = state.actionItems.map((item) =>
          item.id === id ? next : item,
        );
        if (event)
          state.actionItemEvents = replaceRows(state.actionItemEvents, event);
      });
      if (scope && operation && event)
        scheduleActionItemPatch(
          {
            scope,
            actorId,
            operation,
            actionItemId: id,
            patch,
            clearFields,
            optimistic: { items: [next], events: [event], reviewedEvidenceKeys: [] },
            prior: { items: [previous], events: [], reviewedEvidenceKeys: [] },
            onStorageFailure: restoreAfterActionItemStorageFailure(set, scope),
          },
          patchActionItem,
        );
    },
    closeActionItem: (id, closeReason) => {
      const previous = get().actionItems.find((item) => item.id === id);
      if (!previous) return;
      const actorId = getEffectiveUserId() ?? "unknown";
      const next = closeActionItem(previous, { closeReason, actorId });
      const scope = scopeOf(get());
      const operation =
        scope &&
        createActionItemWriteOperation(scope, actorId, `actionItems/${id}`);
      const event =
        operation &&
        optimisticEvent(
          operation.id,
          id,
          "item_closed",
          "Action item lifecycle changed.",
          actorId,
        );
      set((state) => {
        state.actionItems = state.actionItems.map((item) =>
          item.id === id ? next : item,
        );
        if (event)
          state.actionItemEvents = replaceRows(state.actionItemEvents, event);
      });
      if (scope && operation && event)
        queue(() =>
          patchActionItem({
            scope,
            actorId,
            operation,
            actionItemId: id,
            patch: { lifecycle: "closed", closeReason },
            clearFields: [],
            optimistic: { items: [next], events: [event], reviewedEvidenceKeys: [] },
            prior: { items: [previous], events: [], reviewedEvidenceKeys: [] },
            onStorageFailure: restoreAfterActionItemStorageFailure(set, scope),
          }),
        );
    },
    reopenActionItem: (id) => {
      const previous = get().actionItems.find((item) => item.id === id);
      if (!previous) return;
      const actorId = getEffectiveUserId() ?? "unknown";
      const next = reopenActionItem(previous, { actorId });
      const scope = scopeOf(get());
      const operation =
        scope &&
        createActionItemWriteOperation(scope, actorId, `actionItems/${id}`);
      const event =
        operation &&
        optimisticEvent(
          operation.id,
          id,
          "item_reopened",
          "Action item lifecycle changed.",
          actorId,
        );
      set((state) => {
        state.actionItems = state.actionItems.map((item) =>
          item.id === id ? next : item,
        );
        if (event)
          state.actionItemEvents = replaceRows(state.actionItemEvents, event);
      });
      if (scope && operation && event)
        queue(() =>
          patchActionItem({
            scope,
            actorId,
            operation,
            actionItemId: id,
            patch: { lifecycle: "active" },
            clearFields: [],
            optimistic: { items: [next], events: [event], reviewedEvidenceKeys: [] },
            prior: { items: [previous], events: [], reviewedEvidenceKeys: [] },
            onStorageFailure: restoreAfterActionItemStorageFailure(set, scope),
          }),
        );
    },
    setActionItemsFromRemote: (items) => set({ actionItems: items }),
    setActionItemEventsFromRemote: (events) =>
      set({ actionItemEvents: events }),
    setActionItemDrafts: (drafts) => set({ actionItemDrafts: drafts }),
    setActionItemsManagementResource: ({ status, reviewedEvidenceKeys }) =>
      set({ actionItemsManagementStatus: status, reviewedActionItemEvidenceKeys: reviewedEvidenceKeys }),
    clearActionItems: () => {
      clearScheduledActionItemPatches();
      set({
        actionItems: [],
        actionItemEvents: [],
        actionItemDrafts: [],
        actionItemsManagementStatus: "loading",
        reviewedActionItemEvidenceKeys: [],
      });
    },
  },
});

export { clearScheduledActionItemPatches };
